// stagedUploadsStore.ts
import { useSyncExternalStore } from "react";

// Use this type for input to addFile (no id, scanId or status)
export type StagedFileInput = { name: string; size: number };

// Discriminated union for StagedFile
export type StagedFile =
  { id: number; name: string; size: number; status: StagedFileStatus; scanId: number | null };

export type StagedUpload = { requirement: string | null; files: StagedFile[] };

export const STAGED_FILE_STATUS = {
  UPLOADING: 'UPLOADING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  SANITISED: 'SANITISED',
  SCANNING: 'SCANNING',
  FILE_SIZE_EX: 'MAX_SIZE_EXCEEDED',
  NOT_SUPPORTED: 'UNSUPPORTED_FILE',
  VULNERABLE: 'VULNERABLE',
} as const;

export type StagedFileStatus = typeof STAGED_FILE_STATUS[keyof typeof STAGED_FILE_STATUS];

const listeners = new Set<() => void>();

let state: StagedUpload[] = [];
let nextId = 1;

function emit() { for (const l of Array.from(listeners)) l(); }

function withBucket(requirement: string | null, fn: (u: StagedUpload, idx: number) => void) {
  const i = state.findIndex(s => s.requirement === requirement);
  if (i === -1) return;
  const u = state[i];
  fn(u, i);
}

export const stagedUploadsActions = {
  addFile(requirement: string | null, file: StagedFileInput): number {
    const stagedFile: StagedFile = { ...file, scanId: null, id: nextId++, status: STAGED_FILE_STATUS.UPLOADING };
    const i = state.findIndex(s => s.requirement === requirement);
    if (i === -1) state = [...state, { requirement, files: [stagedFile] }];
    else {
      const u = state[i];
      const files = [...u.files, stagedFile];
      state = [...state]; state[i] = { requirement, files };
    }
    emit();
    return stagedFile.id;
  },

  removeFile(requirement: string | null, file: StagedFile) {
    const i = state.findIndex(s => s.requirement === requirement);
    if (i !== -1) {
      const u = state[i];
      const files = u.files.filter(f => f.id !== file.id);
      if (files.length > 0) {
        state = [...state];
        state[i] = { requirement, files };
      } else {
        state = [...state.slice(0, i), ...state.slice(i + 1)];
      }
      emit();
    }
  },

  updateFile(requirement: string | null, file: StagedFile) {
    const i = state.findIndex(s => s.requirement === requirement);
    if (i !== -1) {
      const u = state[i];
      const j = u.files.findIndex(f => f.id === file.id);
      if (j !== -1) {
        const files = [...u.files];
        files[j] = file;
        state = [...state];
        state[i] = { requirement, files };
        emit();
      }
    }
  },

  clear() { state = []; emit(); },
  
  updateById(requirement: string | null, id: number, patch: Partial<StagedFile>) {
    withBucket(requirement, (u, i) => {
      const j = u.files.findIndex(f => f.id === id);
      if (j === -1) return;
      const next = [...u.files];
      next[j] = { ...next[j], ...patch } as StagedFile;
      state = [...state];
      state[i] = { requirement, files: next };
      emit();
    });
  },

  removeById(requirement: string | null, id: number) {
    withBucket(requirement, (u, i) => {
      const files = u.files.filter(f => f.id !== id);
      state = files.length
        ? (state = [...state], state[i] = { requirement, files }, state)
        : [...state.slice(0, i), ...state.slice(i + 1)];
      emit();
    });
  },
};

export function useStagedUploadsStore<T>(
  selector: (s: StagedUpload[]) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
) {
  // subscribe to store changes only
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => selector(state),
    () => selector([]) // SSR fallback
  );
}
